import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Grid, IconButton, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  getDetailedUploadHistorySearch,
  IUploadHistoryRequest,
  IUploadHistoryDetailsResponse,
} from 'wisestep-api';
import {
  WsDataTable,
  WsTypography,
  WsTypoColors,
  WsTitleBanner,
  WsTypoVariants,
  timeSince,
} from 'wisestep-base';

interface HistoryResponse {
  historyData: IUploadHistoryDetailsResponse[];
  totalCount: number;
}

type UploadHistoryParams = {
  batchId: string;
};

const UploadHistoryStatus = () => {
  const params = useParams<UploadHistoryParams>();
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState<IUploadHistoryRequest>({
    size: 50,
    page: 0,
    sort: 'created_on,desc',
    workspaceId: '',
    batchId: params.batchId,
    status: '',
  });

  const cellStatusDetails = (cell: Cell) => {
    switch (cell.value) {
      case 'FAILED':
        return 'Not Processed';
      case 'SUCCESS':
        return 'Processed Successfully';
      case 'Email Not Available':
        return 'Not Processed';
      case 'Imposed Image':
        return 'Not Processed';
      case 'Unique Identifier Not Available':
        return 'Not Processed';
      default:
        return 'PROCESSING';
    }
  };

  const getReason = (cell: Cell) => {
    switch (cell.value) {
      case 'FAILED':
        return 'Unable to Process';
      case 'Email Not Available':
        return 'Email Extraction Failed (Update the email and try again)';
      case 'Unique Identifier Not Available':
        return 'Unique Identifier Not Available (Update the email and try again)';
      case 'Imposed Image':
        return 'Text Extraction Failed(Possibly Image File)';
      case 'WAITING_FOR_EXTRACTION':
        return 'In-Queue';
      case 'PROCESSING':
        return 'In-Queue';
      default:
        return '';
    }
  };

  const [history, setHistory] = useState<HistoryResponse>({ historyData: [], totalCount: 0 });

  const columns = [
    {
      Header: 'File Name',
      accessor: 'resumeName',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>
          {cell?.value.length > 40 ? cell.value.slice(0, 30) + '...' : cell.value}
        </WsTypography>
      ),
    },
    {
      Header: 'Source',
      accessor: 'importFrom',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>{cell?.value ?? 'LOCAL'}</WsTypography>
      ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>{cellStatusDetails(cell)}</WsTypography>
      ),
    },
    {
      Header: 'Reason',
      accessor: 'reason',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>{getReason(cell)}</WsTypography>
      ),
    },
    {
      Header: 'Created on',
      accessor: 'createdOn',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY} variant={WsTypoVariants.SUB_TITLE}>
          {timeSince(cell.value.toString())}
        </WsTypography>
      ),
    },
  ];

  const customData = useMemo(() => {
    return (history.historyData || []).map((history) => ({
      ...history,
      reason: history.status,
    }));
  }, [history]);

  const openTalentpool = () => navigate(`/candidates/`);

  useEffect(() => {
    (async () => {
      const uploadHistory = await getDetailedUploadHistorySearch(query);
      setHistory({
        historyData: uploadHistory.data,
        totalCount: Number(
          uploadHistory.headers['x-total-count'] ?? uploadHistory.headers['X-Total-Count']
        ),
      });
    })();
  }, [query]);

  return (
    <Grid>
      <WsTitleBanner
        title="Bulk Upload Files Status"
        goBack={() => {
          if (location.pathname.includes('/gdrive')) {
            navigate('/import-candidates/bulk-upload-report/gdrive');
          } else {
            navigate('/import-candidates/bulk-upload-report');
          }
        }}
        rightChildren={
          <Stack width="40%" justifyContent="right" direction="row" spacing={2}>
            <Tooltip title={'View In Talent Pool'}>
              <IconButton onClick={openTalentpool}>
                <OpenInNewIcon color="primary" fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />
      <Grid container justifyContent="flex-end" pt={2}>
        <Grid item pt={2}>
          <WsTypography variant={WsTypoVariants.TITLE}>Filter By Status : </WsTypography>
        </Grid>
        <Grid item spacing={2} alignItems="center">
          <Select
            value={query.status}
            onChange={(e) => setQuery({ ...query, status: e.target.value as string })}
            sx={{
              height: '3vw',
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="FAILED,Email Not Available,Imposed Image,Unique Identifier Not Available">
              Not Processed
            </MenuItem>
            <MenuItem value="SUCCESS">Processed Successfully</MenuItem>
            <MenuItem value="PROCESSING">Processing</MenuItem>
          </Select>
        </Grid>
      </Grid>

      <Grid item xs={12} container justifyContent="flex-start" pt={2}>
        <WsDataTable
          columns={columns}
          data={customData}
          totalDataItems={history.totalCount}
          selectable={false}
          boldItems={['fileName']}
          fetchPage={(page) => setQuery((query) => ({ ...query, page: page }))}
        />
      </Grid>
    </Grid>
  );
};

export default UploadHistoryStatus;
